import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import yaml from 'js-yaml';
import Typography from '@mui/material/Typography';
import SubmitButton from 'components/Submits/SubmitButton';
import { isEmpty, isEqual } from 'lodash';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { CodeTextField } from './CodeTextField';
import { ScopesProtectionsInfoModalButton } from './ScopesProtectionsInfoModalButton';

export const StyScopesWrapper = styled(Box, {
    name: 'StyScopesWrapper',
})({
    display: 'flex',
    flexDirection: 'column',
    margin: '1rem',
    gap: '1rem',
    alignItems: 'flex-start',
});

function CustomScopes(props: {
    selectedBind: string;
    missingScopes: string[];
}): React.ReactElement {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const currentScopesPerBind = useSelector(
        (vision) => vision.support.currentScopesPerBind,
    );
    const defaultScopesPerBind = useSelector(
        (vision) => vision.support.defaultScopesPerBind,
    );
    const customScopesPerBind = useSelector(
        (vision) => vision.support.customScopesPerBind,
    );
    const [customScopes, setCustomScopes] = useState<string | null>(null);

    useEffect(() => {
        if (
            Object.prototype.hasOwnProperty.call(customScopesPerBind, 'scopes')
        ) {
            setCustomScopes(yaml.safeDump(customScopesPerBind));
        }
    }, [customScopesPerBind]);

    const submitScopes = () => {
        if (!customScopes) return;
        dispatch.support.updateCustomScopes({
            bind: props.selectedBind,
            scopes: yaml.safeLoad(customScopes),
        });
    };

    const handleScopes = (e: React.ChangeEvent<HTMLInputElement>) =>
        setCustomScopes(e.target.value);
    const missingScopesMessage = t('support.scopes.missingScopes', {
        missingScopes: props.missingScopes,
    });
    return (
        <StyScopesWrapper>
            <Typography variant="h5">
                {t('support.scopes.titleScope')}
            </Typography>
            <ScopesProtectionsInfoModalButton
                buttonTitle={t('support.scopes.defaultScopes')}
                modalTitle={t('support.scopes.defaultScopes')}
                modalContent={yaml.safeDump(defaultScopesPerBind)}
            />
            <CodeTextField
                multiline
                value={customScopes}
                fullWidth
                onChange={handleScopes}
                aria-label="empty textarea"
                rows={20}
                variant="filled"
                placeholder={t('support.scopes.insertCustomScopes')}
            />
            <ScopesProtectionsInfoModalButton
                buttonTitle={t('support.scopes.currentScopes')}
                modalTitle={t('support.scopes.currentScopes')}
                modalContent={yaml.safeDump(currentScopesPerBind)}
            />
            <SubmitButton
                sx={{ mr: '2rem' }}
                submit={submitScopes}
                disabled={isEmpty(currentScopesPerBind)}
                buttonLabel={t('support.scopes.updateScopes')}
                dialogTitle={t('support.scopes.updateScopes')}
            >
                Do you want to really update the scopes of the client{' '}
                <strong>{props.selectedBind}</strong>?
            </SubmitButton>
            {props.missingScopes.length > 0 && (
                <Typography sx={{ color: (theme) => theme.palette.orange }}>
                    ⚠️ {missingScopesMessage}
                </Typography>
            )}
        </StyScopesWrapper>
    );
}

export default memo(CustomScopes, isEqual);
